import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useViewport } from '../../hooks/useViewport';
import { useWebSocket } from '../../hooks/useWebSocket';
import { unifiedApiService } from '../../services/unified';
import { usePublicSystemSettings } from '../../hooks/unified/useSystemQueries';
import { UserDashboardLayout } from '../../components/layout/UserDashboardLayout';
import { 
  ConversationList, 
  MessageThread, 
  MessageSearch 
} from '../../components/messages';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import type { Conversation, Message } from '../../types/profile';

export function MessagesPage() {
  const { user } = useAuth();
  const { isMobile } = useViewport();
  const { data: systemSettings } = usePublicSystemSettings();
  const companyName = systemSettings?.application_name?.value || 'Support Team';
  
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Message[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  // Mobile: show conversation list by default, hide when viewing thread
  const [showConversationList, setShowConversationList] = useState(true);

  // WebSocket connection
  const {
    isConnected,
    connectionState,
    sendMessage: sendWebSocketMessage,
    uploadFile: uploadWebSocketFile,
    sendTyping,
  } = useWebSocket({
    autoConnect: true,
    onMessage: handleWebSocketMessage,
    onTyping: handleTypingIndicator,
    onError: (err) => {
      console.error('WebSocket error:', err);
    },
  });

  // Handle incoming WebSocket messages
  function handleWebSocketMessage(message: any) {
    console.log('Received WebSocket message:', message);
    
    // Transform backend message to frontend Message format
    const transformedMessage: Message = {
      id: message.id,
      conversationId: selectedConversation?.id || user?.id || '',
      senderId: message.sender === 'user' ? (user?.id || '') : 'admin',
      senderName: message.sender === 'user' ? (user?.name || 'You') : companyName,
      senderType: message.sender as 'user' | 'admin',
      content: message.message,
      timestamp: message.created_at,
      read: message.is_read || false,
      attachments: message.attachments?.map((att: any) => ({
        id: att.id,
        filename: att.original_filename || att.filename,
        url: `/api/attachments/${att.filename}`,
        type: att.mime_type || att.content_type || 'application/octet-stream',
        size: att.file_size || 0,
      })) || [],
    };

    // Add message to the list
    setMessages((prev) => {
      // Avoid duplicates
      if (prev.some((m) => m.id === transformedMessage.id)) {
        return prev;
      }
      return [...prev, transformedMessage];
    });

    // Update conversation's last message
    if (selectedConversation) {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === selectedConversation.id
            ? {
                ...conv,
                lastMessage: {
                  id: transformedMessage.id,
                  conversationId: transformedMessage.conversationId,
                  senderId: transformedMessage.senderId,
                  senderName: transformedMessage.senderName,
                  senderType: transformedMessage.senderType,
                  content: transformedMessage.content,
                  timestamp: transformedMessage.timestamp,
                  read: transformedMessage.read,
                },
                updatedAt: transformedMessage.timestamp,
              }
            : conv
        )
      );
    }
  }

  // Handle typing indicators
  function handleTypingIndicator(typing: any) {
    console.log('Typing indicator:', typing);
    
    if (typing.is_typing) {
      setTypingUsers((prev) => new Set(prev).add(typing.user_id));
      
      // Auto-clear after 3 seconds
      setTimeout(() => {
        setTypingUsers((prev) => {
          const next = new Set(prev);
          next.delete(typing.user_id);
          return next;
        });
      }, 3000);
    } else {
      setTypingUsers((prev) => {
        const next = new Set(prev);
        next.delete(typing.user_id);
        return next;
      });
    }
  }

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await unifiedApiService.messaging.getConversations();
      
      console.log('Loaded conversations/messages:', response);
      
      // Handle both array response and object with messages property
      const rawMessages = Array.isArray(response) ? response : (response as any).messages || [];
      
      console.log('Extracted messages array:', rawMessages);
      
      // Transform messages into a single conversation (user-to-admin)
      if (rawMessages && rawMessages.length > 0) {
        const lastMessage = rawMessages[rawMessages.length - 1];
        // For now, assume all admin messages are unread (backend doesn't track read status yet)
        const unreadCount = rawMessages.filter((m: any) => m.sender === 'admin').length;
        
        const userId = user?.id || 'default';
        const conversation: Conversation = {
          id: userId,
          participantIds: [userId, 'admin'],
          lastMessage: {
            id: lastMessage.id || '',
            conversationId: userId,
            senderId: lastMessage.sender === 'user' ? userId : 'admin',
            senderName: lastMessage.sender === 'user' ? (user?.name || 'You') : companyName,
            senderType: lastMessage.sender as 'user' | 'admin',
            content: lastMessage.message || '',
            timestamp: lastMessage.created_at || new Date().toISOString(),
            read: false, // Backend doesn't have is_read field yet
          },
          unreadCount,
          createdAt: rawMessages[0].created_at || new Date().toISOString(),
          updatedAt: lastMessage.created_at || new Date().toISOString(),
        };
        
        setConversations([conversation]);
        
        // Transform all messages for display
        const transformedMessages: Message[] = rawMessages.map((msg: any) => {
          console.log('Transforming message:', { id: msg.id, message: msg.message, content: msg.content, sender: msg.sender, attachments: msg.attachments });
          return {
            id: msg.id,
            conversationId: userId,
            senderId: msg.sender === 'user' ? userId : 'admin',
            senderName: msg.sender === 'user' ? (user?.name || 'You') : companyName,
            senderType: msg.sender as 'user' | 'admin',
            content: msg.message || msg.content || '',
            timestamp: msg.created_at,
            read: msg.is_read || false,
            attachments: (msg.attachments || []).map((att: any) => ({
              id: att.id,
              filename: att.original_filename || att.filename,
              url: `/api/attachments/${att.filename}`,
              type: att.mime_type || att.content_type || 'application/octet-stream',
              size: att.file_size || 0,
            })),
          };
        });
        
        console.log('Transformed messages:', transformedMessages);
        console.log('Sample message content:', transformedMessages[0]?.content);
        setMessages(transformedMessages);
        
        // Auto-select the conversation if not already selected
        setSelectedConversation(conversation);
        
        // Mark messages as read when loading
        if (unreadCount > 0) {
          try {
            await unifiedApiService.messaging.markConversationRead();
            // Update the conversation with 0 unread
            conversation.unreadCount = 0;
            setConversations([conversation]);
          } catch (err) {
            console.error('Failed to mark messages as read:', err);
          }
        }
      } else {
        setConversations([]);
        setMessages([]);
      }
    } catch (err) {
      const error = err as Error;
      console.error('Failed to load conversations:', err);
      setError(error.message || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.name, companyName]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleSendMessage = async (content: string, attachments?: File[]) => {
    if (!selectedConversation) return;

    try {
      if (attachments && attachments.length > 0 && isConnected) {
        // Send files via WebSocket
        for (const file of attachments) {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Data = (reader.result as string).split(',')[1];
            uploadWebSocketFile({
              filename: file.name,
              mime_type: file.type,
              data: base64Data,
              conversation_id: selectedConversation.id,
            });
          };
          reader.readAsDataURL(file);
        }
        
        // Also send text message if provided
        if (content.trim()) {
          sendWebSocketMessage({
            content,
            conversation_id: selectedConversation.id,
          });
        }
      } else if (isConnected) {
        // Use WebSocket for text messages (instant delivery)
        sendWebSocketMessage({
          content,
          conversation_id: selectedConversation.id,
        });
      } else {
        // Fallback to REST API if WebSocket not connected
        await unifiedApiService.messaging.sendMessage({
          message: content,
        } as any);
        await loadConversations();
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      const results = await unifiedApiService.messaging.searchMessages({
        query,
        conversation_id: selectedConversation?.id,
      });
      setSearchResults(results.messages as any);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleConversationSelect = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setSearchQuery('');
    setSearchResults([]);
    
    // On mobile, hide conversation list when selecting a conversation
    if (isMobile) {
      setShowConversationList(false);
    }
    
    // Mark messages as read when conversation is selected
    if (conversation.unreadCount > 0) {
      try {
        await unifiedApiService.messaging.markConversationRead();
        // Update local state to reflect read status
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversation.id 
              ? { ...conv, unreadCount: 0 }
              : conv
          )
        );
      } catch (err) {
        console.error('Failed to mark conversation as read:', err);
      }
    }
  };

  // Handle back navigation on mobile
  const handleBackToList = () => {
    setShowConversationList(true);
    setSelectedConversation(null);
  };

  const totalUnreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (error) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Messages</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadConversations}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div className="p-4 sm:p-6">
        {/* Header - Mobile optimized */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Messages</h1>
            {totalUnreadCount > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {totalUnreadCount} Unread
              </span>
            )}
            {/* WebSocket Connection Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionState === 'connected' ? 'bg-green-500' :
                connectionState === 'connecting' || connectionState === 'reconnecting' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`} />
              <span className="text-xs text-gray-500">
                {connectionState === 'connected' ? 'Live' :
                 connectionState === 'connecting' ? 'Connecting...' :
                 connectionState === 'reconnecting' ? 'Reconnecting...' :
                 'Offline'}
              </span>
            </div>
          </div>
          <p className="text-sm sm:text-base text-gray-600">
            Chat with our support team in real-time.
          </p>
        </div>

        {/* Messages Container - Full screen on mobile */}
        <div className="bg-white shadow rounded-lg overflow-hidden" style={{ height: isMobile ? 'calc(100vh - 200px)' : 'calc(100vh - 250px)' }}>
          <div className="flex h-full">
            {/* Conversations Sidebar - Full width on mobile when showing list */}
            <div className={`${
              isMobile 
                ? showConversationList ? 'w-full' : 'hidden' 
                : 'w-1/3'
            } border-r border-gray-200 flex flex-col`}>
              <div className="p-3 sm:p-4 border-b border-gray-200">
                <MessageSearch
                  query={searchQuery}
                  onSearch={handleSearch}
                  isSearching={isSearching}
                  results={searchResults}
                />
              </div>
              <div className="flex-1 overflow-y-auto mobile-scroll">
                {conversations.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 px-4">
                    <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No conversations</h3>
                    <p className="mt-1 text-sm text-gray-500 mb-4">
                      Have questions? Start a conversation with our team.
                    </p>
                    <button
                      onClick={async () => {
                        try {
                          await unifiedApiService.messaging.sendMessage({
                            message: "Hello! I'd like to get in touch.",
                          } as any);
                          await loadConversations();
                        } catch (err) {
                          console.error('Failed to start conversation:', err);
                        }
                      }}
                      className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 min-h-[44px]"
                    >
                      Start Conversation
                    </button>
                  </div>
                ) : (
                  <ConversationList
                    conversations={conversations}
                    selectedConversation={selectedConversation}
                    onConversationSelect={handleConversationSelect}
                  />
                )}
              </div>
            </div>

            {/* Message Thread - Full width on mobile when viewing thread */}
            <div className={`${
              isMobile 
                ? showConversationList ? 'hidden' : 'w-full' 
                : 'flex-1'
            } flex flex-col`}>
              {selectedConversation ? (
                <>
                  {/* Mobile back button */}
                  {isMobile && (
                    <div className="flex items-center px-3 py-2 border-b border-gray-200 bg-gray-50">
                      <button
                        onClick={handleBackToList}
                        className="flex items-center text-gray-600 hover:text-gray-900 min-h-[44px] min-w-[44px] -ml-2 px-2"
                      >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="text-sm font-medium">Back</span>
                      </button>
                    </div>
                  )}
                  <MessageThread
                    conversation={selectedConversation}
                    messages={messages}
                    loading={loading}
                    onSendMessage={handleSendMessage}
                    currentUserId={user?.id || ''}
                    onTyping={(isTyping) => {
                      if (selectedConversation && isConnected) {
                        sendTyping(selectedConversation.id, isTyping);
                      }
                    }}
                    typingUsers={typingUsers}
                  />
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center px-4">
                    <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Select a conversation</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Choose a conversation from the sidebar to start messaging.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </UserDashboardLayout>
  );
}